<template>
    <div style="w-full">
        <div class="b_clear_banner">
            <hero-banner :image="heroImage" :limit-width="limitWidth" />

            <div class="container">
                <back-button v-if="!$device.isDesktop" :items="breadcrumbs" />

                <div class="banner_content">
                    <div class="banner_content_inner w100">
                        <div class="content">
                            <breadcrumbs
                                v-if="$device.isDesktop"
                                :items="breadcrumbs"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="b_cr_research_list section">
            <div class="background_decor inset-0">
                <div class="page_decor page_decor_right">
                    <div class="image_decor">
                        <picture
                            v-full-image-path:assets="'decor_event_01.svg'"
                        />
                    </div>
                </div>
                <div class="page_decor page_decor_left">
                    <div class="image_decor">
                        <picture
                            v-full-image-path:assets="'decor_event_02.svg'"
                        />
                    </div>
                </div>
            </div>
            <div class="container">
                <div class="top_part">
                    <div class="page_title">
                        <h1>{{ category.title }}</h1>
                    </div>
                    <div class="search_wrapper search_wrapper_category">
                        <div class="search_form relative">
                            <input
                                v-model="searchingText"
                                class="form_control"
                                type="text"
                                :placeholder="`${$t('header.searchButton')}...`"
                            />
                            <button
                                class="btn btn_gray btn_search"
                                type="button"
                                @click.prevent="search"
                            >
                                {{ $t('header.searchButton') }}
                            </button>
                        </div>
                    </div>
                    <div class="short_content">
                        <p>
                            {{
                                $t(
                                    'clinicalResearches.listPage.clinicalStudiesInfo'
                                )
                            }}
                            <span class="phone">
                                :<a href="tel:03-5721608">03-5721608</a>
                            </span>
                        </p>
                    </div>
                </div>
                <div class="research_title">
                    {{ data.value.aboutSection.header }}
                </div>
                <div class="research_wrapper flex row">
                    <div class="research_column col-md-8">
                        <div class="list_wrapper">
                            <div
                                v-for="{
                                    id,
                                    name,
                                    slug,
                                    numericId
                                } in data.articles"
                                :key="id"
                                class="list_row"
                            >
                                <nuxt-link
                                    class="row_link box_shadow"
                                    :to="
                                        $getArticleLink(
                                            { id, slug, numericId },
                                            { clinicalResearch: category.slug }
                                        )
                                    "
                                >
                                    {{ name }}
                                </nuxt-link>
                            </div>
                        </div>
                    </div>
                    <div class="sidebar_column col-md-4">
                        <div class="sidebar_block">
                            <div class="article_card">
                                <div class="article_row text-decoration-none">
                                    <article class="card_block">
                                        <nuxt-link
                                            class="card_head text-decoration-none"
                                            :to="data.value.aboutSection.link"
                                        >
                                            <div class="article_image">
                                                <picture
                                                    v-full-image-path="
                                                        data.value.aboutSection
                                                            .icon
                                                    "
                                                />
                                            </div>
                                            <div class="article_description">
                                                <h3>
                                                    {{
                                                        data.value.aboutSection
                                                            .title
                                                    }}
                                                </h3>
                                                <div
                                                    v-html="
                                                        data.value.aboutSection
                                                            .text
                                                    "
                                                />
                                            </div>
                                        </nuxt-link>
                                        <div class="more_wrap self_end">
                                            <nuxt-link
                                                class="link_arrow"
                                                :to="
                                                    data.value.aboutSection.link
                                                "
                                            >
                                                {{ $t('general.learnMore') }}
                                            </nuxt-link>
                                        </div>
                                    </article>
                                </div>
                            </div>
                            <div class="icon_links_wrapper">
                                <div
                                    v-for="(button, index) in data.value
                                        .categoryButtonsSection"
                                    :key="index"
                                    class="link_row box_shadow"
                                >
                                    <Link class="link" :to="button.link">
                                        <div class="icon">
                                            <picture
                                                v-full-image-path:assets="
                                                    'icon_link_blue.svg'
                                                "
                                            />
                                        </div>
                                        <div class="title">
                                            {{ button.title }}
                                        </div>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Link from '@/components/elements/Link';
import BackButton from '@/components/elements/BackButton';
import HeroBanner from '@/components/elements/HeroBanner';
import Breadcrumbs from '@/components/elements/Breadcrumbs';

export default {
    components: {
        Link,
        BackButton,
        HeroBanner,
        Breadcrumbs
    },

    props: {
        category: {
            type: Object,
            required: true
        },

        data: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            searchingText: ''
        };
    },

    computed: {
        pageTitle() {
            return this.$t('clinicalResearches.pageTitle');
        },

        heroImage() {
            return this.$device.isDesktop
                ? this.data.value.aboutSection.hero.image
                : this.data.value.aboutSection.hero.mobileImage;
        },

        limitWidth() {
            return this.data.value.aboutSection.hero.limitWidth;
        },

        breadcrumbs() {
            return [
                {
                    text: this.$t('homepage.home'),
                    to: this.localePath('/')
                },
                {
                    text: this.pageTitle,
                    to: this.localePath('/clinical-researches')
                },
                {
                    text: this.category.title,
                    active: true
                }
            ];
        }
    },

    methods: {
        search() {
            this.$emit('search', this.searchingText);

            if (!this.$device.isDesktop) {
                const element = document.querySelector('.research_title');

                if (element) {
                    const yOffset = -100;
                    const y =
                        element.getBoundingClientRect().top +
                        window.pageYOffset +
                        yOffset;

                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            }
        }
    }
};
</script>
